import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useLanguage } from '../../context/LanguageContext';

// ── Animated canvas background ──────────────────────────────────────────────
const AfricaNetworkCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Africa-inspired node positions (normalised 0-1)
    const africaPoints = [
      [0.42, 0.12], [0.50, 0.10], [0.58, 0.13], [0.65, 0.18],
      [0.38, 0.22], [0.46, 0.20], [0.54, 0.22], [0.62, 0.25],
      [0.70, 0.22], [0.35, 0.32], [0.44, 0.30], [0.52, 0.32],
      [0.60, 0.30], [0.68, 0.30], [0.74, 0.28], [0.32, 0.42],
      [0.40, 0.40], [0.48, 0.42], [0.56, 0.40], [0.64, 0.42],
      [0.72, 0.38], [0.36, 0.52], [0.44, 0.50], [0.52, 0.52],
      [0.60, 0.50], [0.68, 0.50], [0.40, 0.62], [0.48, 0.60],
      [0.56, 0.62], [0.64, 0.60], [0.44, 0.72], [0.52, 0.70],
      [0.60, 0.72], [0.48, 0.82], [0.54, 0.80],
    ];

    type Node = {
      x: number; y: number;
      vx: number; vy: number;
      r: number; alpha: number;
      pulse: number; pulseSpeed: number;
    };

    let nodes: Node[] = [];
    let animId: number;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      nodes = africaPoints.map(([nx, ny]) => ({
        x: nx * canvas.width,
        y: ny * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2.5 + 1.5,
        alpha: Math.random() * 0.5 + 0.4,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.02 + 0.01,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update positions (gentle drift)
      nodes.forEach(n => {
        n.x += n.vx;
        n.y += n.vy;
        n.pulse += n.pulseSpeed;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      });

      const maxDist = Math.min(canvas.width, canvas.height) * 0.22;

      // Draw edges
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            const opacity = (1 - dist / maxDist) * 0.25;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(212, 160, 23, ${opacity})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      nodes.forEach(n => {
        const pulsedR = n.r + Math.sin(n.pulse) * 1.2;
        // Outer glow
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, pulsedR * 4);
        grad.addColorStop(0, `rgba(212, 160, 23, ${n.alpha * 0.6})`);
        grad.addColorStop(1, 'rgba(212, 160, 23, 0)');
        ctx.beginPath();
        ctx.arc(n.x, n.y, pulsedR * 4, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
        // Core dot
        ctx.beginPath();
        ctx.arc(n.x, n.y, pulsedR, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 160, 23, ${n.alpha})`;
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: 'block' }}
    />
  );
};

// ── Gold divider ─────────────────────────────────────────────────────────────
const GoldDivider = () => (
  <div className="w-full flex items-center justify-center py-0">
    <div className="w-full h-px bg-gradient-to-r from-transparent via-[#D4A017] to-transparent opacity-60" />
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const HomePage = () => {
  const { t } = useLanguage();

  const institutionalServices = [t('home.offers.institutional.desc')];
  const studioServices = [t('home.offers.studio.desc')];

  const stats = [
    { number: '+20 ans', label: t('home.why.experience.desc').split(' ')[0] + ' ' + t('home.why.experience.desc').split(' ')[1] },
    { number: '8', label: 'pays africains' },
    { number: '100%', label: 'hébergement souverain' },
  ];

  const prototypes = [
    {
      title: t('caseStudies.transport.title'),
      badge: 'Transport & Mobilité',
      description: t('caseStudies.transport.desc'),
      features: [
        t('caseStudies.transport.highlight1'),
        t('caseStudies.transport.highlight2'),
        t('caseStudies.transport.highlight3'),
      ],
      tech: ['Flutter', 'Next.js', 'PostgreSQL'],
    },
    {
      title: 'Portail E-Gouvernement',
      badge: 'Gouvernance Publique',
      description: 'Plateforme d\'intégration multi-ministérielle avec contrôle d\'accès basé sur les rôles',
      features: ['Portail services citoyens', 'Workflows inter-ministériels', 'Gestion documentaire'],
      tech: ['Next.js', 'Supabase', 'Cloud'],
    },
    {
      title: 'Tableau de Bord Budgétaire',
      badge: 'Finances Publiques',
      description: 'Analytics temps réel pour la gestion des finances publiques avec hébergement souverain',
      features: ['Analytics temps réel', 'Reporting conformité', 'Accès multi-niveaux'],
      tech: ['React', 'Big Data', 'AI'],
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Lato, sans-serif' }}>
      <Header />

      {/* ── Hero — full viewport height with animated network ── */}
      <section className="relative w-full overflow-hidden" style={{ height: '100vh', background: '#0B1628' }}>
        <AfricaNetworkCanvas />

        {/* Radial vignette overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 70% at 50% 50%, transparent 30%, rgba(11,22,40,0.75) 100%)',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight max-w-5xl">
            {t('home.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl mb-12 max-w-4xl text-gray-300 leading-relaxed">
            {t('home.hero.subtitle')}
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#D4A017] text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-[#B8860B] transition-colors cursor-pointer whitespace-nowrap"
          >
            Parler de votre projet
          </Link>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-50">
          <span className="text-xs text-gray-400 tracking-widest uppercase">Découvrir</span>
          <i className="ri-arrow-down-line text-[#D4A017] text-xl animate-bounce"></i>
        </div>
      </section>

      {/* ── Gold divider ── */}
      <GoldDivider />

      {/* ── Two Offers — white background ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1B2A4A] mb-4">
              {t('home.offers.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Institutional Consulting */}
            <div className="bg-gradient-to-br from-[#1B2A4A] to-[#2A3F5F] p-10 rounded-2xl shadow-xl text-white flex flex-col">
              <div className="w-16 h-16 bg-[#D4A017] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-government-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4">{t('home.offers.institutional.title')}</h3>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                {t('home.offers.institutional.target')}
              </p>
              <div className="space-y-3 flex-1">
                {institutionalServices.map((service, index) => (
                  <div key={index} className="flex items-start">
                    <i className="ri-check-line text-[#D4A017] text-xl mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-gray-200">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Digital Studio */}
            <div className="bg-white border-2 border-[#1B2A4A] p-10 rounded-2xl shadow-xl flex flex-col">
              <div className="w-16 h-16 bg-[#D4A017] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-code-box-line text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4 text-[#1B2A4A]">{t('home.offers.studio.title')}</h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                {t('home.offers.studio.target')}
              </p>
              <div className="space-y-3 flex-1">
                {studioServices.map((service, index) => (
                  <div key={index} className="flex items-start">
                    <i className="ri-check-line text-[#D4A017] text-xl mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Gold divider ── */}
      <GoldDivider />

      {/* ── Stats Bar ── */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-[#D4A017] mb-2">
                  {stat.number}
                </div>
                <p className="text-gray-300 text-lg">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Gold divider ── */}
      <GoldDivider />

      {/* ── Case Studies Preview ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1B2A4A] mb-4">
              Nos Réalisations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Solutions conçues et développées par notre équipe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {prototypes.map((prototype, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#D4A017] transition-colors"
              >
                <div className="p-8">
                  <div className="inline-block bg-[#D4A017] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                    {prototype.badge}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1B2A4A] mb-3">{prototype.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{prototype.description}</p>
                  <div className="space-y-2 mb-6">
                    {prototype.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start text-sm">
                        <i className="ri-arrow-right-s-line text-[#D4A017] mr-2 mt-0.5"></i>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {prototype.tech.map((tech, idx) => (
                      <span key={idx} className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
